import type {
  AiFacilitatorResult,
  CriterionType,
  PackageType,
  PerspectiveType,
  UsageStatus,
  WorkspaceStatus,
  WorkshopAnswers
} from "../../types";
import type { BilingualText, BilingualRecord } from "../i18n/bilingualContent";
import type { WorkspaceRetentionStatus } from "../contentLibrary";
import type { DepartmentValueInputStatus, WorkspaceProcessStatus } from "../valueWorkflow";
import type { CustomerValueCriterionKind, CustomerValueScoringType, SegmentDefinition } from "../valueWorkflow";
import type {
  CustomerValueCriterionCategory,
  CustomerValueCriterionDefinition,
  CustomerValueCriterionScoringBand,
  CustomerValueScoringConfigurationDraftStatus,
  CustomerValueModelStorageType,
  CustomerValueModelType
} from "../customerValue/criterionScoring";
import type {
  CustomerValueCriterionDescriptionSuggestion,
  CustomerValueCriterionSuggestionLanguage
} from "../customerValue/criterionFinalization";
import type { CustomerValueModelReadiness } from "../customerValue/modelReadiness";

export interface RuntimeWorkspace {
  id: string;
  name: string;
  organizationName: string;
  packageType: PackageType;
  status: WorkspaceStatus;
  startDate: string;
  endDate: string;
  goal?: string;
  goalI18n?: BilingualText;
  maxDepartments: number;
  retention?: WorkspaceRetentionStatus;
}

export interface RuntimeDepartment {
  id: string;
  workspaceId: string;
  name: string;
  nameI18n?: BilingualText;
  participantEmail?: string;
  participantUserId?: string;
  participantHasPassword?: boolean;
  participantPasswordSetAt?: string;
  perspectiveType?: PerspectiveType;
  isVotingPerspective?: boolean;
  status: string;
  inviteToken: string;
  responseStatus?: string;
  submittedAt?: string;
  answers?: WorkshopAnswers;
  answersI18n?: BilingualRecord<WorkshopAnswers>;
  valueInputStatus?: DepartmentValueInputStatus;
  valueInput?: {
    id?: string;
    rawInput: string;
    rawInputI18n?: BilingualText;
    status: "draft" | "submitted";
    submittedAt?: string;
  };
  weightingStatus?: "draft" | "submitted";
}

export interface RuntimeWorkspaceProcess {
  status: WorkspaceProcessStatus;
  valueRoundOpenedAt?: string;
  valueRoundCompletedAt?: string;
  clustersReadyAt?: string;
  weightingCompletedAt?: string;
  calculationReadyAt?: string;
  calculationConfirmedAt?: string;
}

export interface RuntimeCustomerValueCluster {
  id: string;
  workspaceId: string;
  name: string;
  description: string;
  criterionType: CriterionType;
  criterionKind: CustomerValueCriterionKind;
  scoringType: CustomerValueScoringType;
  unit?: string;
  sourceTerms: string[];
  sourceDepartments: string[];
  suggestedWeight: number;
  ownerApproved: boolean;
  sortOrder: number;
  contentI18n?: BilingualRecord<{
    name: string;
    description: string;
    sourceTerms: string[];
    sourceDepartments: string[];
    unit?: string;
  }>;
}

export type ContributorLifecycleStatus = "open" | "invited" | "active" | "in_progress" | "submitted";

export interface RuntimeWorkspaceOverview {
  workspace?: RuntimeWorkspace;
  departments: RuntimeDepartment[];
  ownerPerspective?: RuntimeDepartment;
  process?: RuntimeWorkspaceProcess;
  clusters?: RuntimeCustomerValueCluster[];
  weighting?: {
    id: string;
    weights: Record<string, number>;
    totalWeight: number;
    status: "draft" | "submitted";
    submittedAt?: string;
  };
  departmentWeighting?: RuntimeDepartmentWeightingOverview;
  usage?: {
    usageStatus: UsageStatus;
  };
  latestSummary?: {
    id: string;
    createdAt: string;
    model: string;
    language?: "de" | "en";
    result?: AiFacilitatorResult;
    resultI18n?: BilingualRecord<AiFacilitatorResult>;
    methodNote?: string;
  };
  calculation?: {
    id: string;
    name?: string;
    status: string;
    methodologyVersion?: string;
    methodNote?: string;
    criteria?: Array<{
      id: string;
      name: string;
      description: string;
      criterionType: CriterionType;
      weight: number;
      sortOrder: number;
      source: string;
      isActive: boolean;
      contentI18n?: BilingualRecord<{
        name: string;
        description: string;
        signals?: string[];
        unit?: string;
      }>;
    }>;
    scoreTemplates?: Array<{
      id: string;
      segmentName: string;
      minScore: number;
      maxScore: number;
      recommendation: string;
      contentI18n?: BilingualRecord<{
        segmentName: string;
        recommendation: string;
      }>;
    }>;
    crmFieldSuggestions?: string[];
    csvColumnSuggestions?: string[];
    segmentCount?: number;
    segments?: SegmentDefinition[];
    segmentsI18n?: BilingualRecord<SegmentDefinition[]>;
    finalEvaluation?: {
      id: string;
      companyId: string;
      companyName: string;
      status: "draft" | "assigned" | "in_progress" | "submitted" | "ready_for_finalization" | "finalized" | "reopened" | "superseded";
      calculationStatus: "not_calculated" | "pending" | "calculated" | "failed";
      finalScore?: number;
      finalSegmentId?: string;
      finalSegmentName?: string;
      finalSegmentRank?: number;
      calculationVersion?: string;
      finalizedAt?: string;
    };
  };
  perspectiveReview?: RuntimePerspectiveReview;
  contentLibrary?: {
    activeItems: number;
    repairNeeded: number;
    deletionRequestedItems: number;
    deletedItems: number;
  };
}

export interface RuntimeBillingEntitlement {
  id?: string;
  workspaceId?: string;
  planId?: string;
  status?: string;
  accessSource?: string;
  includedDepartmentCount?: number;
  purchasedExtraDepartmentCount?: number;
  maxDepartmentCount?: number;
  currentPeriodStart?: string;
  currentPeriodEnd?: string;
  expiresAt?: string;
  reactivationDeadlineAt?: string;
  lastPaymentStatus?: string;
  providerCustomerId?: string;
  providerSubscriptionId?: string;
  productiveAccessAllowed?: boolean;
  accessAllowed: boolean;
}

export interface RuntimeBillingStatus {
  workspace?: {
    id: string;
    name: string;
  };
  billing?: {
    accessAllowed: boolean;
    productiveAccessAllowed?: boolean;
    canReactivate?: boolean;
    canExtendBeforeExpiry?: boolean;
    entitlement?: RuntimeBillingEntitlement;
    entitlements?: RuntimeBillingEntitlement[];
  };
}

export interface RuntimeBillingPlan {
  id: string;
  module?: "guided_digital_access" | "deal_win_engine";
  entitlementType?: "guided_digital_access" | "deal_win_engine";
  displayName: string;
  description: string;
  durationMonths?: number;
  contractTermMonths?: number;
  autoRenewalTermMonths?: number;
  cancellationNoticeDays?: number;
  cancellationNoticeMonths?: number;
  billingInterval: "monthly" | "every_3_months" | "every_6_months" | "yearly";
  priceNetEur: number;
  currency: "eur";
  includedDepartments: number;
  includedUsers?: number;
  extraDepartmentPriceNetEur: number;
  extraUserPriceNetEur?: number;
  extraDepartmentPriceMode: "recurring";
  paymentMode: "subscription";
  isRecommended?: boolean;
  isBestValue?: boolean;
  renewalDiscountPercent: number;
  reactivationDiscountPercent: number;
}

export interface RuntimeBillingPlansResponse {
  plans: RuntimeBillingPlan[];
  recommendedPlanId: string;
  bestValuePlanId: string;
}

export interface RuntimeBillingCheckoutResponse {
  checkoutUrl: string;
  billingSessionId: string;
  message?: string;
}

export interface RuntimeCustomerValueSegmentDefinition {
  id: string;
  modelId: string;
  modelVersion: number;
  name: string;
  description: string;
  rank: number;
  minScore: number;
  maxScore: number;
  minInclusive: boolean;
  maxInclusive: boolean;
  isBest: boolean;
  isWorst: boolean;
  active: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface RuntimeCustomerValueSegmentDefinitionResponse {
  modelStatus: string;
  modelVersion: number;
  segmentVersion: number;
  status: "draft" | "ready_for_confirmation" | "confirmed" | "superseded";
  editable: boolean;
  validation: {
    ok: boolean;
    errors: string[];
  };
  segments: RuntimeCustomerValueSegmentDefinition[];
}

export interface RuntimeCustomerValueCriterionConfiguration extends CustomerValueCriterionDefinition {
  legacyPolarity?: CriterionType;
  contentI18n?: unknown;
}

export type RuntimeCustomerValueCriterionManagementOperation =
  | "create_category"
  | "update_category"
  | "deactivate_category"
  | "delete_category"
  | "create_criterion"
  | "update_criterion"
  | "duplicate_criterion"
  | "deactivate_criterion"
  | "reactivate_criterion"
  | "delete_criterion"
  | "replace_legacy_with_atomic_draft";

export interface RuntimeCustomerValueConfigurationResponse {
  model: {
    id: string;
    status: string;
    modelType: CustomerValueModelStorageType;
    modelTypeLocked: boolean;
  };
  steps: string[];
  categories: CustomerValueCriterionCategory[];
  criteria: RuntimeCustomerValueCriterionConfiguration[];
  bandsByCriterionId: Record<string, CustomerValueCriterionScoringBand[]>;
  validation: {
    ok: boolean;
    weightTotal: number;
    errors: string[];
  };
  readiness: CustomerValueModelReadiness;
  updatedActiveWeight: number;
  weightDifferenceTo100: number;
  scoringConfigurationStatus: CustomerValueScoringConfigurationDraftStatus;
  finalizationAllowed: boolean;
  criterion?: RuntimeCustomerValueCriterionConfiguration;
  scoringBands?: CustomerValueCriterionScoringBand[];
  confirmAvailable: boolean;
  confirmMessage: string;
}

export interface RuntimeCustomerValueConfigurationPreview {
  ok: boolean;
  modelId: string;
  modelType: CustomerValueModelStorageType;
  score?: number;
  rawScore?: number;
  roundedScore?: number;
  engineVersion?: string;
  scoreScale?: {
    min: number;
    max: number;
    criterionPointMin: number;
    criterionPointMax: number;
    weightingUnit: string;
    rounding: string;
  };
  segment?: {
    id: string;
    name: string;
    minScore: number;
    maxScore: number;
    minInclusive: boolean;
    maxInclusive: boolean;
  };
  segmentId?: string | null;
  contributions: Array<{
    criterionId: string;
    points: number;
    normalizedScore: number;
    weight: number;
    weightedContribution: number;
  }>;
  criterionResults?: unknown[];
  readiness?: unknown;
  blockingReasons?: string[];
  inputSnapshot?: unknown;
  missingValues: unknown[];
  missingScoringLogic?: unknown[];
  missingCriterionScores?: unknown[];
  notComputableCriteria?: unknown[];
  errors: string[];
  draftVersion: number;
}

export interface RuntimeCustomerValueFinalizationResponse {
  ok: true;
  companyEvaluationId: string;
  snapshotId: string;
  calculationVersion: string;
  finalScore: number;
  finalSegmentId: string;
  finalizedAt: string;
}

export interface RuntimeCustomerValueFinalCriterionResult {
  criterionId: string;
  criterionName?: string;
  rawValue: unknown;
  normalizedValue: string | number | boolean | null;
  scoringBandId: string | null;
  scoringBandVersion: number | null;
  points: number | null;
  weight: number | null;
  weightedContribution: number | null;
  status: "computed" | "blocked" | "ignored_inactive";
  reasonCode: string | null;
}

export interface RuntimeCustomerValueFinalResult {
  id: string;
  companyEvaluationId: string;
  evaluationBatchId: string;
  companyId: string;
  companyName?: string;
  modelId: string;
  modelName?: string;
  modelVersion: number;
  status: "finalized";
  engineVersion: string;
  calculationVersion: string;
  rawScore: number | null;
  roundedScore: number;
  finalScore: number;
  scoreScale: { min: number; max: number };
  segmentId: string | null;
  segmentName: string;
  segmentRank: number;
  weightingVersion: number | null;
  scoringBandVersions: Record<string, number>;
  criterionResults: RuntimeCustomerValueFinalCriterionResult[];
  confidence: number;
  completeness: number;
  source: string;
  calculatedAt: string;
  finalizedAt: string;
  createdAt: string;
}

export interface RuntimeCustomerValueFinalResultResponse {
  result: RuntimeCustomerValueFinalResult;
}

export interface RuntimeCustomerValueFinalResultHistoryResponse {
  results: RuntimeCustomerValueFinalResult[];
}

export interface RuntimeDepartmentWeightingSubmission {
  id: string;
  departmentId: string;
  departmentName: string;
  perspectiveType?: PerspectiveType;
  status: "draft" | "submitted" | "missing";
  weights: Record<string, number>;
  totalWeight: number;
  submittedAt?: string;
}

export interface RuntimeDepartmentWeightingOverview {
  criteriaSignature: string;
  totalDepartments: number;
  submittedDepartments: number;
  averages: Record<string, number>;
  submissions: RuntimeDepartmentWeightingSubmission[];
  missingDepartments: Array<{ id: string; name: string; perspectiveType?: PerspectiveType }>;
}

export interface RuntimeDepartmentWeightingDetail {
  department: RuntimeDepartment;
  workspace: RuntimeWorkspace;
  process?: RuntimeWorkspaceProcess;
  criteriaSignature: string;
  clusters: RuntimeCustomerValueCluster[];
  weighting?: {
    id: string;
    weights: Record<string, number>;
    totalWeight: number;
    status: "draft" | "submitted";
    submittedAt?: string;
  };
}

export interface RuntimePerspectiveReview {
  missingExecutive: boolean;
  missingFinanceOrCommercial: boolean;
  acknowledgedAt?: string;
}

export interface ContributorWorkspaceOverview {
  departments: Array<RuntimeDepartment & { workspaceName: string }>;
  process?: RuntimeWorkspaceProcess;
}

export interface RuntimeDepartmentDetail {
  department: RuntimeDepartment;
  workspace: RuntimeWorkspace;
  process?: RuntimeWorkspaceProcess;
}

export interface ContributorActionResult {
  department: RuntimeDepartment;
  setupLink?: string;
  expiresAt?: string;
  emailSent?: boolean;
  emailError?: string;
}

export type ContributorInviteResult = ContributorActionResult & {
  setupLink: string;
  expiresAt: string;
};

async function parseJson<T>(response: Response): Promise<T> {
  return (await response.json()) as T;
}

async function assertOk(response: Response): Promise<void> {
  if (response.ok) return;
  const data = await response.json().catch(() => undefined) as { error?: string; code?: string; message?: string } | undefined;
  throw new Error(data?.code ?? data?.error ?? data?.message ?? "Daten konnten nicht geladen werden.");
}

export async function fetchRuntimeWorkspace(): Promise<RuntimeWorkspaceOverview> {
  const response = await fetch("/api/workspace", { credentials: "include" });
  await assertOk(response);
  return parseJson<RuntimeWorkspaceOverview>(response);
}

export async function requestRuntimeWorkspaceContentDeletion(): Promise<RuntimeWorkspace["retention"]> {
  const response = await fetch("/api/workspace/content/deletion-request", {
    method: "POST",
    credentials: "include"
  });
  await assertOk(response);
  const data = await parseJson<{ retention?: RuntimeWorkspace["retention"] }>(response);
  return data.retention;
}

export async function fetchRuntimeBillingEntitlement(): Promise<RuntimeBillingStatus> {
  const response = await fetch("/api/billing/entitlement", { credentials: "include" });
  await assertOk(response);
  return parseJson<RuntimeBillingStatus>(response);
}

export async function fetchRuntimeBillingPlans(): Promise<RuntimeBillingPlansResponse> {
  const response = await fetch("/api/billing/plans", { credentials: "include" });
  await assertOk(response);
  return parseJson<RuntimeBillingPlansResponse>(response);
}

export async function startRuntimeBillingCheckout(input: {
  purpose: "initial_purchase" | "add_department" | "upgrade" | "renewal" | "reactivation";
  planId: string;
  extraDepartmentCount?: number;
  extraUserCount?: number;
  discountAction?: "upgrade_15" | "renewal_15";
}): Promise<RuntimeBillingCheckoutResponse> {
  const endpoint = input.purpose === "add_department"
    ? "/api/billing/checkout/add-department"
    : input.purpose === "upgrade"
      ? "/api/billing/checkout/upgrade"
    : input.purpose === "renewal"
      ? "/api/billing/checkout/renew"
      : input.purpose === "reactivation"
        ? "/api/billing/checkout/reactivate"
        : "/api/billing/checkout";
  const response = await fetch(endpoint, {
    method: "POST",
    headers: { "content-type": "application/json" },
    credentials: "include",
    body: JSON.stringify({
      planId: input.planId,
      extraDepartmentCount: input.extraDepartmentCount ?? 0,
      extraUserCount: input.extraUserCount ?? 0,
      discountAction: input.discountAction
    })
  });
  await assertOk(response);
  const data = await parseJson<Partial<RuntimeBillingCheckoutResponse> & { url?: string; checkout_url?: string }>(response);
  if (typeof data.checkoutUrl !== "string" || !data.checkoutUrl.startsWith("https://")) {
    throw new Error("Checkout konnte nicht gestartet werden.");
  }
  return {
    checkoutUrl: data.checkoutUrl,
    billingSessionId: data.billingSessionId ?? "",
    message: data.message
  };
}

export async function openRuntimeBillingPortal(
  flow?: "invoices" | "payment-method" | "cancel-subscription" | "update-subscription"
): Promise<{ portalUrl: string }> {
  const suffix = flow ? `/${flow}` : "";
  const response = await fetch(`/api/billing/portal${suffix}`, {
    method: "POST",
    credentials: "include"
  });
  await assertOk(response);
  return parseJson<{ portalUrl: string }>(response);
}

export async function generateRuntimeAiSummary(
  language?: "de" | "en",
  options?: { regenerate?: boolean }
): Promise<RuntimeWorkspaceOverview["latestSummary"]> {
  const body = language || options?.regenerate ? JSON.stringify({ language, regenerate: Boolean(options?.regenerate) }) : undefined;
  const response = await fetch("/api/workspace/hauffe-os-summary/generate", {
    method: "POST",
    headers: body ? { "content-type": "application/json" } : undefined,
    body,
    credentials: "include"
  });
  await assertOk(response);
  const data = await parseJson<{ summary: RuntimeWorkspaceOverview["latestSummary"] }>(response);
  return data.summary;
}

export async function generateRuntimeCustomerValueCalculation(): Promise<RuntimeWorkspaceOverview["calculation"]> {
  const response = await fetch("/api/workspace/customer-value/generate", {
    method: "POST",
    credentials: "include"
  });
  await assertOk(response);
  const data = await parseJson<{ calculation: RuntimeWorkspaceOverview["calculation"] }>(response);
  return data.calculation;
}

export async function generateRuntimeCustomerValueClusters(
  language?: "de" | "en",
  options?: { regenerate?: boolean }
): Promise<RuntimeCustomerValueCluster[]> {
  const body = language || options?.regenerate ? JSON.stringify({ language, regenerate: Boolean(options?.regenerate) }) : undefined;
  const response = await fetch("/api/workspace/customer-value/clusters/generate", {
    method: "POST",
    headers: body ? { "content-type": "application/json" } : undefined,
    body,
    credentials: "include"
  });
  await assertOk(response);
  const data = await parseJson<{ clusters: RuntimeCustomerValueCluster[] }>(response);
  return data.clusters;
}

export async function openRuntimeValueRound(): Promise<RuntimeWorkspaceProcess> {
  const response = await fetch("/api/workspace/value-round/open", {
    method: "POST",
    credentials: "include"
  });
  await assertOk(response);
  const data = await parseJson<{ process: RuntimeWorkspaceProcess }>(response);
  return data.process;
}

export async function updateRuntimeCustomerValueCluster(
  clusterId: string,
  input: {
    name: string;
    description: string;
    criterionType: CriterionType;
    criterionKind?: CustomerValueCriterionKind;
    scoringType?: CustomerValueScoringType;
    unit?: string;
    ownerApproved?: boolean;
    language?: "de" | "en";
  }
): Promise<RuntimeCustomerValueCluster> {
  const response = await fetch(`/api/workspace/customer-value/clusters/${encodeURIComponent(clusterId)}`, {
    method: "PATCH",
    headers: { "content-type": "application/json" },
    credentials: "include",
    body: JSON.stringify(input)
  });
  await assertOk(response);
  const data = await parseJson<{ cluster: RuntimeCustomerValueCluster }>(response);
  return data.cluster;
}

export async function deleteRuntimeCustomerValueCluster(clusterId: string): Promise<void> {
  const response = await fetch(`/api/workspace/customer-value/clusters/${encodeURIComponent(clusterId)}`, {
    method: "DELETE",
    credentials: "include"
  });
  await assertOk(response);
}

export async function approveRuntimeCustomerValueClusters(): Promise<RuntimeWorkspaceProcess> {
  const response = await fetch("/api/workspace/customer-value/clusters/approve", {
    method: "POST",
    credentials: "include"
  });
  await assertOk(response);
  const data = await parseJson<{ process: RuntimeWorkspaceProcess }>(response);
  return data.process;
}

export async function saveRuntimeCustomerValueWeights(
  weights: Record<string, number>,
  segmentCount: number,
  language?: "de" | "en"
): Promise<RuntimeWorkspaceOverview["calculation"]> {
  const response = await fetch("/api/workspace/customer-value/weights", {
    method: "POST",
    headers: { "content-type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ weights, segmentCount, language })
  });
  await assertOk(response);
  const data = await parseJson<{ calculation: RuntimeWorkspaceOverview["calculation"] }>(response);
  return data.calculation;
}

export async function getRuntimeCustomerValueSegmentDefinition(modelId: string): Promise<RuntimeCustomerValueSegmentDefinitionResponse> {
  const response = await fetch(`/api/workspace/customer-value/models/${encodeURIComponent(modelId)}/segments`, {
    method: "GET",
    credentials: "include"
  });
  await assertOk(response);
  return parseJson<RuntimeCustomerValueSegmentDefinitionResponse>(response);
}

export async function createRuntimeCustomerValueSegmentDraft(
  modelId: string,
  clientRequestId: string
): Promise<RuntimeCustomerValueSegmentDefinitionResponse> {
  const response = await fetch(`/api/workspace/customer-value/models/${encodeURIComponent(modelId)}/segments/draft`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ clientRequestId })
  });
  await assertOk(response);
  return parseJson<RuntimeCustomerValueSegmentDefinitionResponse>(response);
}

export async function updateRuntimeCustomerValueSegmentDraft(
  modelId: string,
  input: { expectedSegmentVersion: number; segments: RuntimeCustomerValueSegmentDefinition[] }
): Promise<RuntimeCustomerValueSegmentDefinitionResponse> {
  const response = await fetch(`/api/workspace/customer-value/models/${encodeURIComponent(modelId)}/segments/draft`, {
    method: "PUT",
    headers: { "content-type": "application/json" },
    credentials: "include",
    body: JSON.stringify(input)
  });
  await assertOk(response);
  return parseJson<RuntimeCustomerValueSegmentDefinitionResponse>(response);
}

export async function confirmRuntimeCustomerValueSegmentDraft(
  modelId: string,
  expectedSegmentVersion: number
): Promise<RuntimeCustomerValueSegmentDefinitionResponse> {
  const response = await fetch(`/api/workspace/customer-value/models/${encodeURIComponent(modelId)}/segments/confirm`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ expectedSegmentVersion })
  });
  await assertOk(response);
  return parseJson<RuntimeCustomerValueSegmentDefinitionResponse>(response);
}

export async function getRuntimeCustomerValueConfiguration(modelId: string): Promise<RuntimeCustomerValueConfigurationResponse> {
  const response = await fetch(`/api/workspace/customer-value/models/${encodeURIComponent(modelId)}/configuration`, {
    method: "GET",
    credentials: "include"
  });
  await assertOk(response);
  return parseJson<RuntimeCustomerValueConfigurationResponse>(response);
}

export async function updateRuntimeCustomerValueConfiguration(
  modelId: string,
  input: {
    modelType?: CustomerValueModelType;
    operation?: RuntimeCustomerValueCriterionManagementOperation;
    category?: Partial<CustomerValueCriterionCategory> & { id?: string };
    criterion?: Partial<CustomerValueCriterionDefinition> & { id?: string; scoringBands?: CustomerValueCriterionScoringBand[] };
    sourceCriterionId?: string;
    criteria?: Array<Partial<CustomerValueCriterionDefinition> & { id: string }>;
    bandsByCriterionId?: Record<string, CustomerValueCriterionScoringBand[]>;
  }
): Promise<RuntimeCustomerValueConfigurationResponse> {
  const response = await fetch(`/api/workspace/customer-value/models/${encodeURIComponent(modelId)}/configuration`, {
    method: "PUT",
    headers: { "content-type": "application/json" },
    credentials: "include",
    body: JSON.stringify(input)
  });
  await assertOk(response);
  return parseJson<RuntimeCustomerValueConfigurationResponse>(response);
}

export function createRuntimeCustomerValueCategory(
  modelId: string,
  category: Pick<CustomerValueCriterionCategory, "name" | "rank"> & Partial<CustomerValueCriterionCategory>
): Promise<RuntimeCustomerValueConfigurationResponse> {
  return updateRuntimeCustomerValueConfiguration(modelId, { operation: "create_category", category });
}

export function updateRuntimeCustomerValueCategory(
  modelId: string,
  category: Pick<CustomerValueCriterionCategory, "id"> & Partial<CustomerValueCriterionCategory>
): Promise<RuntimeCustomerValueConfigurationResponse> {
  return updateRuntimeCustomerValueConfiguration(modelId, { operation: "update_category", category });
}

export function deleteRuntimeCustomerValueCategory(
  modelId: string,
  categoryId: string
): Promise<RuntimeCustomerValueConfigurationResponse> {
  return updateRuntimeCustomerValueConfiguration(modelId, { operation: "delete_category", category: { id: categoryId } });
}

export function createRuntimeCustomerValueCriterion(
  modelId: string,
  criterion: Pick<CustomerValueCriterionDefinition, "name" | "criterionType" | "weight" | "rank"> & Partial<CustomerValueCriterionDefinition> & { scoringBands?: CustomerValueCriterionScoringBand[] }
): Promise<RuntimeCustomerValueConfigurationResponse> {
  return updateRuntimeCustomerValueConfiguration(modelId, { operation: "create_criterion", criterion });
}

export function duplicateRuntimeCustomerValueCriterion(
  modelId: string,
  sourceCriterionId: string,
  criterion?: Partial<CustomerValueCriterionDefinition>
): Promise<RuntimeCustomerValueConfigurationResponse> {
  return updateRuntimeCustomerValueConfiguration(modelId, { operation: "duplicate_criterion", sourceCriterionId, criterion });
}

export function setRuntimeCustomerValueCriterionActive(
  modelId: string,
  criterionId: string,
  active: boolean
): Promise<RuntimeCustomerValueConfigurationResponse> {
  return updateRuntimeCustomerValueConfiguration(modelId, {
    operation: active ? "reactivate_criterion" : "deactivate_criterion",
    criterion: { id: criterionId }
  });
}

export function deleteRuntimeCustomerValueCriterion(
  modelId: string,
  criterionId: string
): Promise<RuntimeCustomerValueConfigurationResponse> {
  return updateRuntimeCustomerValueConfiguration(modelId, { operation: "delete_criterion", criterion: { id: criterionId } });
}

export async function previewRuntimeCustomerValueConfiguration(
  modelId: string,
  valuesByCriterionId: Record<string, unknown>
): Promise<RuntimeCustomerValueConfigurationPreview> {
  const response = await fetch(`/api/workspace/customer-value/models/${encodeURIComponent(modelId)}/configuration/preview`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ valuesByCriterionId })
  });
  await assertOk(response);
  return parseJson<RuntimeCustomerValueConfigurationPreview>(response);
}

export async function requestRuntimeCustomerValueCriterionDescriptionSuggestion(
  modelId: string,
  input: {
    criterionName: string;
    modelType: CustomerValueModelType;
    evaluationType: CustomerValueCriterionDefinition["criterionType"];
    valueType: CustomerValueCriterionDefinition["valueType"];
    unitType: CustomerValueCriterionDefinition["unitType"];
    scoringDirection: CustomerValueCriterionDefinition["scoringDirection"];
    existingDescription?: string | null;
    language?: CustomerValueCriterionSuggestionLanguage;
  }
): Promise<CustomerValueCriterionDescriptionSuggestion> {
  const response = await fetch(`/api/workspace/customer-value/models/${encodeURIComponent(modelId)}/criteria/description-suggestion`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    credentials: "include",
    body: JSON.stringify(input)
  });
  await assertOk(response);
  return parseJson<CustomerValueCriterionDescriptionSuggestion>(response);
}

export async function confirmRuntimeCustomerValueConfiguration(modelId: string): Promise<RuntimeCustomerValueConfigurationResponse> {
  const response = await fetch(`/api/workspace/customer-value/models/${encodeURIComponent(modelId)}/configuration/confirm`, {
    method: "POST",
    credentials: "include"
  });
  await assertOk(response);
  return parseJson<RuntimeCustomerValueConfigurationResponse>(response);
}

export async function finalizeRuntimeCustomerValueCompanyEvaluation(
  companyEvaluationId: string
): Promise<RuntimeCustomerValueFinalizationResponse> {
  const response = await fetch(`/api/workspace/customer-value/company-evaluations/${encodeURIComponent(companyEvaluationId)}/finalize`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ source: "existing_customer_value" })
  });
  await assertOk(response);
  return parseJson<RuntimeCustomerValueFinalizationResponse>(response);
}

export const finalizeCompanyEvaluation = finalizeRuntimeCustomerValueCompanyEvaluation;

export async function fetchRuntimeCustomerValueCompanyEvaluationResult(
  companyEvaluationId: string
): Promise<RuntimeCustomerValueFinalResult> {
  const response = await fetch(`/api/workspace/customer-value/company-evaluations/${encodeURIComponent(companyEvaluationId)}/result`, {
    method: "GET",
    credentials: "include"
  });
  await assertOk(response);
  const data = await parseJson<RuntimeCustomerValueFinalResultResponse>(response);
  return data.result;
}

export async function fetchRuntimeCustomerValueCompanyEvaluationResults(
  companyEvaluationId: string
): Promise<RuntimeCustomerValueFinalResult[]> {
  const response = await fetch(`/api/workspace/customer-value/company-evaluations/${encodeURIComponent(companyEvaluationId)}/results`, {
    method: "GET",
    credentials: "include"
  });
  await assertOk(response);
  const data = await parseJson<RuntimeCustomerValueFinalResultHistoryResponse>(response);
  return data.results;
}

export async function sendRuntimeDepartmentWeightingReminder(): Promise<{ sent: number; missing: number }> {
  const response = await fetch("/api/workspace/customer-value/weighting/reminder", {
    method: "POST",
    credentials: "include"
  });
  await assertOk(response);
  return parseJson<{ sent: number; missing: number }>(response);
}

export async function downloadRuntimeCustomerValueExport(language?: "de" | "en"): Promise<Blob> {
  const suffix = language ? `?language=${encodeURIComponent(language)}` : "";
  const response = await fetch(`/api/workspace/customer-value/export${suffix}`, {
    credentials: "include"
  });
  await assertOk(response);
  return response.blob();
}

export async function confirmRuntimeCustomerValueCalculation(): Promise<RuntimeWorkspaceOverview["calculation"]> {
  const response = await fetch("/api/workspace/customer-value/confirm", {
    method: "POST",
    credentials: "include"
  });
  await assertOk(response);
  const data = await parseJson<{ calculation: RuntimeWorkspaceOverview["calculation"] }>(response);
  return data.calculation;
}

export async function fetchRuntimeDepartments(): Promise<RuntimeWorkspaceOverview> {
  const response = await fetch("/api/workspace/departments", { credentials: "include" });
  await assertOk(response);
  return parseJson<RuntimeWorkspaceOverview>(response);
}

export async function createRuntimeDepartment(input: {
  workspaceId: string;
  name: string;
  perspectiveType?: PerspectiveType;
  contributorEmail?: string;
  isVotingPerspective?: boolean;
  language?: "de" | "en";
}) {
  const response = await fetch(`/api/workspaces/${encodeURIComponent(input.workspaceId)}/departments`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    credentials: "include",
    body: JSON.stringify({
      name: input.name,
      perspectiveType: input.perspectiveType,
      contributorEmail: input.contributorEmail,
      isVotingPerspective: input.isVotingPerspective,
      language: input.language
    })
  });
  await assertOk(response);
  return parseJson<{ department: RuntimeDepartment; setupLink?: string; expiresAt?: string; emailSent?: boolean; emailError?: string }>(response);
}

export async function configureRuntimeOwnerPerspective(input: {
  mode: "executive" | "functional" | "organizer_only";
  perspectiveType?: PerspectiveType;
  name?: string;
  language?: "de" | "en";
}): Promise<{ department: RuntimeDepartment }> {
  const response = await fetch("/api/workspace/owner-perspective", {
    method: "POST",
    headers: { "content-type": "application/json" },
    credentials: "include",
    body: JSON.stringify(input)
  });
  await assertOk(response);
  return parseJson<{ department: RuntimeDepartment }>(response);
}

export async function reopenRuntimeValueRound(): Promise<RuntimeWorkspaceProcess> {
  const response = await fetch("/api/workspace/value-round/reopen", {
    method: "POST",
    credentials: "include"
  });
  await assertOk(response);
  const data = await parseJson<{ process: RuntimeWorkspaceProcess }>(response);
  return data.process;
}

export async function acknowledgeRuntimeMissingPerspectives(input: {
  missingExecutive: boolean;
  missingFinanceOrCommercial: boolean;
}): Promise<{ perspectiveReview: RuntimePerspectiveReview }> {
  const response = await fetch("/api/workspace/perspectives/acknowledge-missing", {
    method: "POST",
    headers: { "content-type": "application/json" },
    credentials: "include",
    body: JSON.stringify(input)
  });
  await assertOk(response);
  return parseJson<{ perspectiveReview: RuntimePerspectiveReview }>(response);
}

export async function inviteDepartmentContributor(departmentId: string, email: string): Promise<ContributorInviteResult> {
  const response = await fetch(`/api/workspace/departments/${encodeURIComponent(departmentId)}/invite-contributor`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ email })
  });
  await assertOk(response);
  return parseJson<ContributorInviteResult>(response);
}

export async function updateDepartmentContributorEmail(departmentId: string, email: string): Promise<ContributorInviteResult> {
  const response = await fetch(`/api/workspace/departments/${encodeURIComponent(departmentId)}/contributor-email`, {
    method: "PATCH",
    headers: { "content-type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ email })
  });
  await assertOk(response);
  return parseJson<ContributorInviteResult>(response);
}

export async function replaceDepartmentContributor(departmentId: string, email: string): Promise<ContributorInviteResult> {
  const response = await fetch(`/api/workspace/departments/${encodeURIComponent(departmentId)}/replace-contributor`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ email })
  });
  await assertOk(response);
  return parseJson<ContributorInviteResult>(response);
}

export async function removeDepartmentContributor(departmentId: string): Promise<ContributorActionResult> {
  const response = await fetch(`/api/workspace/departments/${encodeURIComponent(departmentId)}/remove-contributor`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    credentials: "include"
  });
  await assertOk(response);
  return parseJson<ContributorActionResult>(response);
}

export async function resendDepartmentContributorSetupLink(departmentId: string): Promise<ContributorInviteResult> {
  const response = await fetch(`/api/workspace/departments/${encodeURIComponent(departmentId)}/resend-setup-link`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    credentials: "include"
  });
  await assertOk(response);
  return parseJson<ContributorInviteResult>(response);
}

export async function fetchContributorWorkspace(): Promise<ContributorWorkspaceOverview> {
  const response = await fetch("/api/contributor", { credentials: "include" });
  await assertOk(response);
  return parseJson<ContributorWorkspaceOverview>(response);
}

export async function fetchContributorDepartment(departmentId: string): Promise<{
  department: RuntimeDepartment;
  workspace: RuntimeWorkspace;
  process?: RuntimeWorkspaceProcess;
}> {
  const response = await fetch(`/api/contributor/departments/${encodeURIComponent(departmentId)}`, { credentials: "include" });
  await assertOk(response);
  return parseJson<{ department: RuntimeDepartment; workspace: RuntimeWorkspace; process?: RuntimeWorkspaceProcess }>(response);
}

export async function fetchRuntimeOwnerDepartment(departmentId: string): Promise<RuntimeDepartmentDetail> {
  const response = await fetch(`/api/workspace/departments/${encodeURIComponent(departmentId)}`, { credentials: "include" });
  await assertOk(response);
  return parseJson<RuntimeDepartmentDetail>(response);
}

export async function fetchRuntimeDepartmentWeighting(departmentId: string, audience: "owner" | "contributor" = "contributor"): Promise<RuntimeDepartmentWeightingDetail> {
  const prefix = audience === "owner" ? "/api/workspace" : "/api/contributor";
  const response = await fetch(`${prefix}/departments/${encodeURIComponent(departmentId)}/weighting`, { credentials: "include" });
  await assertOk(response);
  return parseJson<RuntimeDepartmentWeightingDetail>(response);
}

export async function saveRuntimeDepartmentWeighting(
  departmentId: string,
  weights: Record<string, number | string | null | undefined>,
  submit: boolean
): Promise<RuntimeDepartmentWeightingDetail["weighting"]> {
  const suffix = submit ? "submit" : "draft";
  const response = await fetch(`/api/departments/${encodeURIComponent(departmentId)}/weighting/${suffix}`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ weights })
  });
  await assertOk(response);
  const data = await parseJson<{ weighting: RuntimeDepartmentWeightingDetail["weighting"] }>(response);
  return data.weighting;
}

export async function saveRuntimeDepartmentResponse(departmentId: string, answers: WorkshopAnswers, submit: boolean, language: "de" | "en" = "de") {
  const suffix = submit ? "submit" : "draft";
  const response = await fetch(`/api/departments/${encodeURIComponent(departmentId)}/responses/${suffix}`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ answers, language })
  });
  await assertOk(response);
  return parseJson<{ response: { id: string; responseStatus: string } }>(response);
}

export async function saveRuntimeDepartmentValueInput(departmentId: string, rawInput: string, submit: boolean, language: "de" | "en" = "de") {
  const suffix = submit ? "submit" : "draft";
  const response = await fetch(`/api/departments/${encodeURIComponent(departmentId)}/value-inputs/${suffix}`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ rawInput, language })
  });
  await assertOk(response);
  return parseJson<{ valueInput: RuntimeDepartment["valueInput"]; process?: RuntimeWorkspaceProcess }>(response);
}
